import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useApiClient } from "@src/packages/api/louise-api";
import { throwApiError } from "@src/packages/api/api-error";
import type {
  ApiClinicQuote,
  CreateClinicQuotePayload,
  UpdateClinicQuotePayload,
} from "../session-cost-records";

export type {
  ApiClinicQuote,
  ClinicQuoteExtraction,
  CreateClinicQuotePayload,
  UpdateClinicQuotePayload,
} from "../session-cost-records";

export function clinicQuotesQueryKey(patientId: string | null) {
  return ["clinic-quotes", patientId] as const;
}

export function useClinicQuotes() {
  const { client, patientId } = useApiClient();

  return useQuery<ApiClinicQuote[]>({
    queryKey: clinicQuotesQueryKey(patientId),
    queryFn: async () => {
      if (!patientId) return [];
      const { data, error, response } = await client.GET(
        "/patients/{patientId}/clinic-quotes",
        { params: { path: { patientId } } },
      );
      if (error) {
        throwApiError(
          error,
          "Failed to load clinic quotes",
          "useClinicQuotes",
          response?.status,
        );
      }
      return data?.data ?? [];
    },
    enabled: !!patientId,
    placeholderData: [],
  });
}

export function useCreateClinicQuote() {
  const { client, patientId } = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: CreateClinicQuotePayload) => {
      if (!patientId) throw new Error("No patient context");
      const { data, error, response } = await client.POST(
        "/patients/{patientId}/clinic-quotes",
        {
          params: { path: { patientId } },
          body: payload,
        },
      );
      if (error) {
        throwApiError(
          error,
          "Failed to create clinic quote",
          "useCreateClinicQuote",
          response?.status,
        );
      }
      if (!data?.data) throw new Error("Clinic quote response was empty");
      return data.data;
    },
    onSuccess: (quote) => {
      if (!patientId) return;
      queryClient.setQueryData<ApiClinicQuote[]>(
        clinicQuotesQueryKey(patientId),
        (current = []) => [
          quote,
          ...current.filter((item) => item.id !== quote.id),
        ],
      );
      void queryClient.invalidateQueries({
        queryKey: clinicQuotesQueryKey(patientId),
      });
    },
  });
}

export function useUpdateClinicQuote() {
  const { client, patientId } = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      clinicQuoteId,
      updates,
    }: {
      clinicQuoteId: string;
      updates: UpdateClinicQuotePayload;
    }) => {
      if (!patientId) throw new Error("No patient context");
      const { data, error, response } = await client.PATCH(
        "/patients/{patientId}/clinic-quotes/{clinicQuoteId}",
        {
          params: { path: { patientId, clinicQuoteId } },
          body: updates,
        },
      );
      if (error) {
        throwApiError(
          error,
          "Failed to update clinic quote",
          "useUpdateClinicQuote",
          response?.status,
        );
      }
      if (!data?.data) throw new Error("Clinic quote response was empty");
      return data.data;
    },
    onSuccess: (quote) => {
      if (!patientId) return;
      queryClient.setQueryData<ApiClinicQuote[]>(
        clinicQuotesQueryKey(patientId),
        (current = []) =>
          current.map((item) => (item.id === quote.id ? quote : item)),
      );
      void queryClient.invalidateQueries({
        queryKey: clinicQuotesQueryKey(patientId),
      });
    },
  });
}

export function useDeleteClinicQuote() {
  const { client, patientId } = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (clinicQuoteId: string) => {
      if (!patientId) throw new Error("No patient context");
      const { error, response } = await client.DELETE(
        "/patients/{patientId}/clinic-quotes/{clinicQuoteId}",
        { params: { path: { patientId, clinicQuoteId } } },
      );
      if (error) {
        throwApiError(
          error,
          "Failed to delete clinic quote",
          "useDeleteClinicQuote",
          response?.status,
        );
      }
    },
    onSuccess: (_result, clinicQuoteId) => {
      if (!patientId) return;
      queryClient.setQueryData<ApiClinicQuote[]>(
        clinicQuotesQueryKey(patientId),
        (current = []) => current.filter((item) => item.id !== clinicQuoteId),
      );
      void queryClient.invalidateQueries({
        queryKey: clinicQuotesQueryKey(patientId),
      });
    },
  });
}
